import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { from, map, Observable, switchMap, tap } from 'rxjs';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { environment } from '@brighthub/environment';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { AuthService } from '@brightside/core/auth';
import { Conversation, Message, MessageRole, Trace, TraceData } from '../models/copilot.models';
import {
  processAgentCoreStream,
  type AgentCoreTraceResult,
  type DoneEvent
} from '../utils/agentcore-stream';
import { CopilotActions } from '../store/copilot.actions';
import { Store } from '@ngrx/store';
import { CopilotState } from '../store/copilot.reducer';
import { MessageService } from 'primeng/api';
import { AmplitudeService, SentryService } from '@brighthub/logging';

@Injectable({
  providedIn: 'root'
})
export class CopilotApiService {
  private _http = inject(HttpClient);
  private _authService = inject(AuthService);
  private _store = inject(Store<CopilotState>);
  private _messageService = inject(MessageService);
  private _sentry = inject(SentryService);
  private _amplitude = inject(AmplitudeService);

  private apiName = 'copilot';

  private _loadMessagesError = signal<string | null>(null);
  readonly loadMessagesError = this._loadMessagesError.asReadonly(); // exposed for facade

  private _channel = new BroadcastChannel('brightnav-copilot');

  constructor() {
    this._channel.onmessage = (event) => {
      const { type, payload } = event.data ?? {};
      if (!type || !payload) return;

      switch (type) {
        case 'new-message':
          this._store.dispatch(CopilotActions.messageReceived(payload));
          break;
        case 'update-message':
          this._store.dispatch(CopilotActions.messageUpdated(payload));
          break;
      }
    };
  }

  broadcast(type: string, payload: any) {
    this._channel.postMessage({ type, payload });
  }

  reportLoadMessagesError(message: string) {
    this._messageService.add({
      severity: 'error',
      summary: 'Failed to load messages',
      detail: message,
    });
  }

  private _getToken(): Observable<string|undefined> {
    return from(this._authService.getAccessToken());
  }

  private _getHttpHeaders(): Observable<HttpHeaders> {
    return this._getToken().pipe(
      map(token => {
        if (!token) {
          this._messageService.add({
            severity: 'error',
            summary: 'Authentication Error',
            detail: 'You are not authenticated. Please log in again.'
          });
          throw new Error('No authentication token available');
        } else {
          return new HttpHeaders({ Authorization: `Bearer ${token}` })
        }
      })
    );
  }

  private _getFetchHeaders(): Observable<Headers> {
    return this._getToken().pipe(
      map((token) => {
        if (!token) {
          this._messageService.add({
            severity: 'error',
            summary: 'Authentication Error',
            detail: 'You are not authenticated. Please log in again.'
          });
          throw new Error('No authentication token available');
        } else {
          return new Headers({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          });
        }
      })
    );
  }

  fetchConversations(clientId: string): Observable<Conversation[]> {
    const url = `${environment.apiEndpoints[this.apiName]}/conversation/list`;
    this._sentry.addBreadcrumb({
      category: 'copilot',
      message: `Fetching conversations for client ${clientId}`,
      data: { clientId, url },
    });
    return this._getHttpHeaders().pipe(
      switchMap(headers =>
        this._http.get<{ items: Conversation[] }>(url, { headers }).pipe(
          map(res => res.items || [])
        )
      )
    );
  }

  fetchMessages(clientId: string, conversationId: string): Observable<Message[]> {
    const url = `${environment.apiEndpoints[this.apiName]}/conversation/${conversationId}/messages`;
    this._sentry.addBreadcrumb({
      category: 'copilot',
      message: `Fetching messages for conversation ${conversationId}`,
      data: { clientId, conversationId, url },
    });
    return this._getHttpHeaders().pipe(
      switchMap((headers) =>
        this._http.get<Conversation>(url, { headers }).pipe(
          map((res) => {

            const messages = res.messages.map(message => {
              return {...message, owner_id: res.owner_id};
            })

            return messages || [];
          })
        )
      )
    );
  }

  sendMessage(clientId: string, conversationId: string, content: string, fileName?: string): Observable<Message> {
    this._amplitude.logEvent(`copilot_send_message`, {
      subjectId: clientId, conversationId
    });
    this._store.dispatch(CopilotActions.startStreaming({ clientId, conversationId }));
    this._sentry.addBreadcrumb({
      category: 'copilot',
      message: `Sending message to conversation ${conversationId}`,
      data: {
        clientId,
        conversationId,
        fileName,
        contentLength: content.length,
      },
    });
    return this._getFetchHeaders().pipe(
      switchMap(headers => {
        const url = `${environment.apiEndpoints[this.apiName]}/conversation/messages/stream`;

        return new Observable<Message>(observer => {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 10000); // 10s timeout

          fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              conversation_id: conversationId,
              content,
              file_name: fileName,
              agent_framework: 'agentcore_strands',
            }),
            signal: controller.signal
          })
            .then(async response => {
              clearTimeout(timeout);

              if (!response.ok || !response.body) throw new Error('Streaming response was not ok or body was missing.');

              const reader = response.body.getReader();
              const assistantMessageId = `assistant-${Date.now()}`;
              let hasDispatchedInitialAssistantMessage = false;
              let hasStreamErrored = false;
              let traces: Trace[] = [];
              let contentSoFar = '';
              const STREAM_PAUSE_MS = 1500;
              let streamPauseTimer: ReturnType<typeof setTimeout> | null = null;

              const stopStreamOnError = () => {
                if (hasStreamErrored) return;
                hasStreamErrored = true;
                controller.abort();
                reader.cancel().catch(() => {});
              };

              const clearStreamPauseTimer = () => {
                if (streamPauseTimer !== null) {
                  clearTimeout(streamPauseTimer);
                  streamPauseTimer = null;
                }
              };

              const scheduleStreamPause = () => {
                clearStreamPauseTimer();
                streamPauseTimer = setTimeout(() => {
                  streamPauseTimer = null;
                  if (hasDispatchedInitialAssistantMessage) {
                    this.updateMsg({
                      clientId,
                      conversationId,
                      messageId: assistantMessageId,
                      content: contentSoFar,
                      traces,
                      metadata: { streamPaused: true }
                    });
                  }
                }, STREAM_PAUSE_MS);
              };

              await processAgentCoreStream({
                reader,
                onChunk: (text: string) => {
                  contentSoFar += text;
                  if (!hasDispatchedInitialAssistantMessage) {
                    const newMessage = {
                      clientId,
                      conversationId,
                      message: {
                        id: assistantMessageId,
                        role: MessageRole.ASSISTANT,
                        content: '',
                        created_at: new Date().toISOString(),
                        conversation_id: conversationId,
                        traces: [],
                        metadata: { finalized: false }
                      }
                    };
                    this._store.dispatch(CopilotActions.messageReceived(newMessage));
                    this._channel.postMessage({ type: 'new-message', payload: newMessage });
                    hasDispatchedInitialAssistantMessage = true;
                  }
                  scheduleStreamPause();
                  this.updateMsg({
                    clientId,
                    conversationId,
                    messageId: assistantMessageId,
                    content: contentSoFar,
                    traces,
                    metadata: { streamPaused: false }
                  });
                },
                onTrace: (trace: AgentCoreTraceResult) => {
                  const t: Trace = {
                    index: traces.length,
                    type: trace.type,
                    reasoning: trace.reasoning,
                    rationale: trace.rationale,
                    toolUsage: trace.toolUsage
                  };
                  traces = [...traces, t];
                  if (hasDispatchedInitialAssistantMessage) {
                    this.updateMsg({
                      clientId,
                      conversationId,
                      messageId: assistantMessageId,
                      content: contentSoFar,
                      traces
                    });
                  }
                },
                onComplete: (done?: DoneEvent) => {
                  clearStreamPauseTimer();
                  if (hasDispatchedInitialAssistantMessage) {
                    this.updateMsg({
                      clientId,
                      conversationId,
                      messageId: assistantMessageId,
                      content: contentSoFar,
                      traces,
                      metadata: {
                        finalized: true,
                        streamPaused: false,
                        ...(done ? { finalId: done['id'] } : {}),
                      },
                    });
                  }
                  this._store.dispatch(CopilotActions.stopStreaming({ clientId, conversationId }));
                  observer.complete();
                },
                onError: (error: string) => {
                  clearStreamPauseTimer();
                  if (hasStreamErrored) return;
                  stopStreamOnError();
                  this._handleStreamError(observer, clientId, conversationId, error, false, fileName);
                }
              });
            })
            .catch(err => {
              console.error('Streaming failed:', err);
              const isTimeout = err.name === 'AbortError';
              this._handleStreamError(observer, clientId, conversationId, undefined, isTimeout, fileName);
            });
        });
      })
    );
  }

  private _handleStreamError(
    observer: any,
    clientId: string,
    conversationId: string,
    serverMessage?: string,
    isTimeout = false,
    fileName?: string,
  ): void {
    this._amplitude.logEvent(`copilot_stream_error`, {
      subjectId: clientId, conversationId, isTimeout
    })
    this._store.dispatch(CopilotActions.stopStreaming({ clientId, conversationId }));
    this._sentry.captureApiError({
      error: new Error(serverMessage || 'Copilot stream error'),
      path: '/conversation/messages/stream',
      method: 'POST',
      apiName: this.apiName,
      requestBody: {
        conversation_id: conversationId,
        client_id: clientId,
        ...(fileName ? { file_name: fileName } : {})
      },
      handled: false,
    });
    const fallbackMessage = serverMessage ||
      (isTimeout
        ? 'The assistant took too long to respond. Please try again.'
        : 'Sorry, something went wrong while processing your message. Please try again.');

    observer.next({
      id: `error-${Date.now()}`,
      role: 'assistant',
      content: fallbackMessage,
      created_at: new Date().toISOString(),
      conversation_id: conversationId,
      metadata: {
        finalized: true,
        error: true
      }
    });

    observer.complete();
  }

  processTrace(traceData: TraceData) {
    const result: {
      reasoning?: string;
      rationale?: string;
      toolUsage?: unknown;
      index?: number;
    } = {};

    try {
      // Check if orchestrationTrace exists
      if (traceData.content?.trace?.orchestrationTrace) {

        const orchestrationTrace = traceData.content.trace.orchestrationTrace;
        // Extract reasoning from modelInvocationOutput if it exists
        if (orchestrationTrace.modelInvocationOutput?.rawResponse) {
          try {
            // Parse rawResponse if it's a string, or use it directly if it's already an object
            const rawResponse = typeof orchestrationTrace.modelInvocationOutput.rawResponse === 'string'
              ? JSON.parse(orchestrationTrace.modelInvocationOutput.rawResponse)
              : orchestrationTrace.modelInvocationOutput.rawResponse;
            // Check for reasoning in different possible locations
            if (rawResponse.content && Array.isArray(rawResponse.content)) {
              // Look for thinking or reasoning in content array
              for (const item of rawResponse.content) {
                if (item.text && item.text.includes('<thinking>')) {
                  const thinkingMatch = item.text.match(/<thinking>([\s\S]*?)<\/thinking>/);
                  if (thinkingMatch && thinkingMatch[1]) {
                    result.reasoning = thinkingMatch[1].trim();
                    break;
                  }
                }
                if (item.reasoningText) {
                  result.reasoning = item.reasoningText;
                  break;
                }
              }
            }
          } catch (e) {
            console.error('Error processing rawResponse:', e);
          }
        }

        if (orchestrationTrace?.invocationInput) {
          try {
            const inputRawResponse = typeof orchestrationTrace.invocationInput === 'string'
              ? JSON.parse(orchestrationTrace.invocationInput)
              : orchestrationTrace.invocationInput;
            if (inputRawResponse.actionGroupInvocationInput) {
              const actionGroupInvocationInput = inputRawResponse.actionGroupInvocationInput;

              result.toolUsage = actionGroupInvocationInput;

            }
          } catch (e) {
            console.error('Error processing invocationInput:', e);
          }
        }

        // Extract rationale if it exists
        if (orchestrationTrace.rationale?.text) {
          result.rationale = orchestrationTrace.rationale.text;
        }
      }
    } catch (error) {
      console.error('Error processing trace data:', error);
    }

    // Check if the traceInfo object is empty
    const isEmpty = Object.keys(result).length === 0;
    if (isEmpty) {
      return null;
    }

    return result;
  }

  private updateMsg(msgUpdate: {
    clientId: string;
    conversationId: string;
    messageId: string;
    content: string;
    traces?: Trace[];
    metadata?: Record<string, unknown>;
  }) {
    this._store.dispatch(CopilotActions.messageUpdated(msgUpdate));
    this._channel.postMessage({ type: 'update-message', payload: msgUpdate });
  }
}
